import { Transition } from '@headlessui/react'
import { ChevronLeftIcon, XMarkIcon } from '@heroicons/react/24/outline'
import classNames from 'classnames'
import { Fragment, ReactElement, useEffect, useRef, useState } from 'react'
import Spinner from '../Spinner'
import { useEscapeListener } from 'app/hooks/useEscapeListener'
import { useIsMobile } from 'app/hooks/useMediaQuery'

type DrawerProps = {
  open?: boolean
  canExpand?: boolean
  isExpanded?: boolean
  title?: string
  extraSlot?: React.ReactElement
  leftContent?: React.ReactElement
  rightContent?: React.ReactElement
  footerSlot?: React.ReactElement
  overlay?: boolean
  onClose: () => void
}

export default function Drawer({
  open,
  canExpand = true,
  isExpanded = false,
  title,
  extraSlot,
  leftContent,
  rightContent,
  footerSlot,
  overlay = true,
  onClose,
}: DrawerProps): ReactElement | null {
  let [expand, setExpand] = useState<boolean>(isExpanded)
  const ref = useRef<HTMLDivElement>(null)
  const isMobile = useIsMobile()
  // On mobile hold the body until the open slide finishes (see DrawerV2) so the
  // full-screen slide stays smooth.
  const [entered, setEntered] = useState(false)
  const showBody = !isMobile || entered
  // oxlint-disable-next-line react/immutability -- existing closure or mutable API pattern is intentional
  useEscapeListener(() => closeDrawerHandler(), open)
  const closeDrawerHandler = () => {
    if (!isExpanded) setExpand(false)
    onClose()
  }

  useEffect(() => {
    // Where the press started. Selecting text inside the drawer and releasing
    // past its edge fires `mouseup` on an outside element, which read as an
    // outside click and closed the drawer mid-drag, losing the selection and the
    // form underneath it. Only a press that both starts and ends outside counts.
    let pressStartedInside = false

    const handleMouseDown = (event: MouseEvent) => {
      pressStartedInside =
        event.target instanceof Element &&
        (!!ref.current?.contains(event.target) ||
          !!event.target.closest(`[data-floating-ui-portal]`) ||
          !!event.target.closest(`[data-radix-popper-content-wrapper]`) ||
          !!event.target.closest(`[data-headlessui-portal]`) ||
          !!event.target.closest(`[data-floating-layer]`))
    }

    const handleClick = (event: MouseEvent) => {
      const startedInside = pressStartedInside
      pressStartedInside = false
      if (startedInside) return
      if (
        event.target instanceof HTMLElement &&
        ref.current &&
        !ref.current.contains(event.target) &&
        !event.target.closest(`[data-floating-ui-portal]`) &&
        // Radix (Popover, DropdownMenu, Select, ...) portals its content to
        // document.body inside a `data-radix-popper-content-wrapper` div, so
        // picking an option in a combobox rendered *inside* the drawer lands
        // outside `ref`. Without this guard, selecting a location in the new
        // contact drawer closed the drawer. DrawerV2 already guards this.
        !event.target.closest(`[data-radix-popper-content-wrapper]`) &&
        !event.target.closest(`[data-headlessui-portal]`) &&
        // The alert row floats over the page, drawer included. Dismissing a card
        // is a press on the row, not on the drawer underneath it.
        !event.target.closest(`[data-floating-layer]`)
      ) {
        closeDrawerHandler()
      }
    }

    window.addEventListener(`mousedown`, handleMouseDown)
    window.addEventListener(`mouseup`, handleClick)

    return () => {
      window.removeEventListener(`mousedown`, handleMouseDown)
      window.removeEventListener(`mouseup`, handleClick)
    }
  }, [ref, closeDrawerHandler])

  return (
    <Transition
      as={Fragment}
      // `appear` runs the enter transition when the drawer mounts with `show`
      // already true (remount while a ?drawer= deep link is active, HMR,
      // conditionally mounted parents), matching DrawerV2. Without it Headless
      // UI skips the enter entirely on such mounts, `afterEnter` never fires,
      // and the mobile body below stays on the placeholder spinner forever.
      appear
      enter="transform transition ease-out duration-[300ms]"
      enterFrom={`opacity-0 translate-x-full`}
      enterTo="opacity-100 translate-x-0"
      leave="transform transition ease-in duration-[300ms]"
      leaveFrom="opacity-100 translate-x-0"
      leaveTo={`opacity-0 translate-x-full`}
      show={open}
      afterEnter={() => setEntered(true)}
      afterLeave={() => setEntered(false)}
    >
      <div
        ref={ref}
        x-component-name="Drawer"
        className={classNames(
          overlay ? `fixed right-0 top-0 z-20 w-full md:w-auto md:shadow-elevation-8` : `ml-4 shadow-elevation-8`,
        )}
      >
        <div
          tabIndex={-1}
          className="flex h-dvh w-full flex-col border border-l-default bg-surface pt-[env(safe-area-inset-top)] text-left transition-all md:h-screen md:w-auto md:pt-0"
        >
          <div className="flex w-full items-center justify-between border-b border-default px-4 py-4">
            {canExpand ? (
              <div
                className="flex cursor-pointer items-center gap-x-2"
                onClick={() => {
                  setExpand((prev) => !prev)
                }}
              >
                {rightContent && (
                  <>
                    <ChevronLeftIcon
                      className={classNames(expand ? `rotate-180 transform` : ``, `h-4 w-4 text-default`)}
                      strokeWidth={1.5}
                    />
                    <span className="text-ui-sm text-default">{`${expand ? `Hide` : `Show`} related objects`}</span>
                  </>
                )}
              </div>
            ) : (
              <>{title && <span className="shrink-0 text-heading-md text-strong">{title}</span>}</>
            )}
            <div className={classNames(`flex items-center gap-x-4`, { 'w-full flex-row-reverse': !canExpand })}>
              {canExpand && expand && (extraSlot ?? null)}
              <XMarkIcon className="h-4 w-4 cursor-pointer text-default" onClick={() => closeDrawerHandler()} />
            </div>
          </div>
          <div className="flex grow flex-col overflow-hidden md:flex-row">
            {showBody ? (
              <>
                <div className="flex min-h-0 w-full grow md:w-auto md:grow-0">{leftContent}</div>
                {expand && <div className="flex w-full grow border-l border-default md:w-auto">{rightContent}</div>}
              </>
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <Spinner size={20} />
              </div>
            )}
          </div>
          {footerSlot ?? null}
        </div>
      </div>
    </Transition>
  )
}
